import { jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getGatewayConfigOverridesFromEnv, getURLParamsGatewayConfigOverrides } from "./gateway-config-overrides.js";
import { DEFAULT_GATEWAY_CONFIGS } from "./gateway-config-model.js";
import { useIsAuthenticated, useIsInternalUser } from "../authentication/index.js";
import { useLocalStorage } from "../browser-storage/index.js";
import { logError } from "../errors/index.js";
import { gql, useQuery } from "../graphql/index.js";
import { GetGatewayConfigsContext } from "../generated/graphql.js";
const GET_GATEWAY_CONFIGS = gql`
  query GetGatewayConfigs {
    gatewayConfigs {
      returnedSeriesLimit
      returnedDatapointsLimit
      tracingUIEnabled
      collectionsAccess
      serviceUIEnabled
      sloUIEnabled
      enableExternalServiceConfig
      enableMetricsDDX
      alertEntityUIEnabled
      aiGatewayEnabled
    }
    tenant {
      tenantConfiguration {
        scrapeInterval
      }
    }
  }
`;
const GatewayConfigsContext = /*#__PURE__*/ createContext({
    isLoading: false,
    originalGatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
    gatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
    setGatewayConfigs: ()=>{},
    reset: ()=>{}
});
function GatewayConfigsContextProvider(props) {
    const isAuthenticated = useIsAuthenticated();
    const [searchParams] = useSearchParams();
    const [{ data, error, fetching }] = useQuery({
        query: GET_GATEWAY_CONFIGS,
        context: GetGatewayConfigsContext,
        pause: !isAuthenticated
    });
    useEffect(()=>{
        if (null == error) return;
        logError(error);
    }, [
        error
    ]);
    const configs = data && !error ? {
        ...data?.gatewayConfigs,
        ...data?.tenant?.tenantConfiguration
    } : DEFAULT_GATEWAY_CONFIGS;
    const isChronoAdmin = useIsInternalUser();
    const envConfigLocalOverrides = getGatewayConfigOverridesFromEnv();
    const [localStorageGatewayConfigs, setLocalStorageGatewayConfigs] = useLocalStorage('gatewayConfigsOverrides', {});
    useEffect(()=>{
        const paramOverrides = getURLParamsGatewayConfigOverrides(searchParams.toString());
        if (Object.keys(paramOverrides).length > 0) {
            if (Object.keys(localStorageGatewayConfigs).length > 0) {
                const confirmed = window.confirm('You have opened a link with URL gateway config overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.');
                if (!confirmed) return;
            }
            setLocalStorageGatewayConfigs(paramOverrides);
        }
    }, []);
    const reset = useCallback(()=>{
        setLocalStorageGatewayConfigs({});
    }, [
        setLocalStorageGatewayConfigs
    ]);
    const mergedConfigs = isChronoAdmin ? {
        ...configs,
        ...envConfigLocalOverrides,
        ...localStorageGatewayConfigs
    } : configs;
    return /*#__PURE__*/ jsx(GatewayConfigsContext.Provider, {
        value: {
            isLoading: fetching,
            originalGatewayConfigs: configs,
            gatewayConfigs: mergedConfigs,
            setGatewayConfigs: setLocalStorageGatewayConfigs,
            reset
        },
        children: props.children
    });
}
function useGatewayConfigsState() {
    const { gatewayConfigs, setGatewayConfigs, originalGatewayConfigs, reset } = useContext(GatewayConfigsContext);
    return {
        originalGatewayConfigs,
        gatewayConfigs,
        setGatewayConfigs,
        reset
    };
}
function useGatewayConfigs() {
    const { gatewayConfigs } = useGatewayConfigsState();
    return gatewayConfigs;
}
function areGatewayConfigsLoading(gatewayConfigs) {
    return gatewayConfigs === DEFAULT_GATEWAY_CONFIGS;
}
export { GatewayConfigsContext, GatewayConfigsContextProvider, areGatewayConfigsLoading, useGatewayConfigs, useGatewayConfigsState };

//# sourceMappingURL=GatewayConfigs.js.map